import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  ORGANIZER_MARKETPLACE_LINK,
  ORGANIZER_NAV_SECTIONS,
  getOrganizerActiveTab,
  isOrganizerNavActive,
} from "./organizerNavConfig";

export default function OrganizerMobileNavDropdown({ className = "" }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const activeTab = getOrganizerActiveTab(location.search);
  const MarketplaceIcon = ORGANIZER_MARKETPLACE_LINK.icon;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className={`relative lg:hidden ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={open ? "Close menu" : "Open menu"}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-white/70 hover:text-white hover:bg-white/[0.05] transition-colors shrink-0"
      >
        <Menu className="w-5 h-5" strokeWidth={2} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 z-50 mt-2 w-[min(calc(100vw-24px),280px)] max-h-[min(70vh,420px)] overflow-y-auto rounded-xl border border-[#1E293B]/60 bg-[#060D1B] shadow-2xl py-2"
          role="listbox"
        >
          {ORGANIZER_NAV_SECTIONS.map((section) => (
            <div key={section.title} className="px-2 pb-1">
              <div className="px-2 py-1.5">
                <span
                  className="text-[9px] text-white/25 uppercase tracking-[0.18em]"
                  style={{ fontWeight: 700 }}
                >
                  {section.title}
                </span>
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isOrganizerNavActive(item, location, activeTab);
                  return (
                    <Link
                      key={`${section.title}-${item.label}`}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all ${
                        active
                          ? "text-white bg-blue-500/[0.12] border border-blue-500/[0.15]"
                          : "text-white/50 hover:text-white hover:bg-white/[0.03] border border-transparent"
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${active ? "text-blue-400" : ""}`} strokeWidth={1.8} />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-1 px-2 pt-2 border-t border-[#1E293B]/60">
            <Link
              to={ORGANIZER_MARKETPLACE_LINK.href}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[12px] text-white/30 hover:text-white/60 hover:bg-white/[0.03] transition-all"
            >
              <MarketplaceIcon className="w-4 h-4" strokeWidth={1.8} />
              {ORGANIZER_MARKETPLACE_LINK.label}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
